import { useEffect, useState } from 'react';
import { breakpoints, mediaQueries } from '../styles/designTokens';

/**
 * The one breakpoint wrapper (ADR-0007 / responsive-design.md).
 *
 * Every hook here answers `matchMedia` against `designTokens.breakpoints` —
 * **the same module `tailwind.config.js` reads** — so behaviour (these hooks)
 * and styling (`md:` / `lg:` classes) are driven by one set of numbers and
 * cannot drift.
 *
 * ## Why not antd's `Grid.useBreakpoint()`, which this used to call
 *
 * Two reasons, both found by measurement rather than by reading.
 *
 * **1. antd's numbers were not ours.** antd's `lg` is 992 and its `xl` is 1200;
 * ours are 1024 and 1280. This file previously asserted that *"antd shares its
 * numbers with Tailwind, so styling and behaviour can never disagree"* — which
 * was false at `lg` and `xl`, and that one sentence is what made several
 * readers (and two briefed agents) measure the wrong numbers. The cost was
 * real: `useIsLargeUp()` was documented as *"true from `lg` (1024px) up"* and
 * was true from 992, so the 44px touch floor was dropped across **992–1023 —
 * tablets, the exact band the floor exists to protect** (QA F-1). Measured
 * before the fix: 44px controls at 991, 32px at 992.
 *
 * `themeConfig.ts` now aligns antd's screen tokens to ours as well, so a direct
 * `Grid.useBreakpoint()` call agrees with these hooks. That alignment is worth
 * having, but it could not be the fix on its own — see reason 2.
 *
 * **2. `Grid.useBreakpoint()` depends on where it is called.** It reads its
 * tokens from the nearest `ConfigProvider`. `app/providers.tsx` decides *which
 * theme to give that provider* by calling {@link useIsLargeUp} **above** it,
 * where the override is not in scope — so the aligned tokens would never have
 * reached the one call that picks the touch theme, and the same hook would have
 * returned different answers in different parts of the tree. `matchMedia` has
 * no such context dependency: it asks the viewport.
 *
 * `components/table/ResponsiveTable.tsx` had already reached this conclusion
 * and kept a private copy of {@link useIsLargeUp} for exactly this reason,
 * noting that the right home was this file. **That local hook is now redundant**
 * and can be deleted in favour of this one — left in place here only because
 * that file is outside this change's ownership.
 *
 * Use these **only** where the rendered DOM differs — mounting a Drawer instead
 * of a Sider, choosing a theme. Pure styling belongs in Tailwind's prefixes.
 */
function useMediaQuery(query: string): boolean {
  /*
    Initialised from a real `matchMedia` read rather than `false`, so the first
    paint is already correct. Starting at `false` would render the phone layout
    for one frame on a desktop, which is a visible flash on every mount and was
    the defect the drawers' own hooks had to work around.
  */
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    // Read once on mount as well: a resize between render and effect would
    // otherwise leave this stale until the next one.
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * True below `md` ({@link breakpoints.md}px) — the mobile↔desktop boundary.
 *
 * Unchanged in behaviour by the `lg` fix: antd's `md` and ours are both 768,
 * which is why this hook was never wrong and why the false claim survived so
 * long — it was true at the one breakpoint most people checked.
 */
export function useIsMobile(): boolean {
  return !useMediaQuery(mediaQueries.atLeastMd);
}

/**
 * True from `lg` ({@link breakpoints.lg}px) up.
 *
 * This is the **touch/pointer boundary**, not a gutter boundary: `md` (768) is
 * where the layout stops being a phone, but 768–1024 is still tablets, so the
 * 44px target floor applies below `lg`, not below `md` (`designTokens.ts`
 * `layout.touchTarget`). The statement is now true at 1024; it previously fired
 * at 992 and dropped the floor on 32px of tablet widths.
 */
export function useIsLargeUp(): boolean {
  return useMediaQuery(mediaQueries.atLeastLg);
}

/**
 * True from `xl` ({@link breakpoints.xl}px) up.
 *
 * Added so the document drawers can stop calling `Grid.useBreakpoint().xl`
 * directly — that reads antd's `xl`, and `GoodsReceiptDetailDrawer` and
 * `PurchaseOrderDetailDrawer` both use it to decide whether their action group
 * fits beside the title. With antd's tokens now aligned those calls give the
 * right answer anyway, so this is a tidying rather than a fix, and switching
 * them over belongs to whoever owns those files.
 */
export function useIsXlUp(): boolean {
  /*
    Composed here rather than read from `mediaQueries`: that map has
    `atLeastMd` and `atLeastLg` but no `atLeastXl`, and adding one means editing
    `designTokens.ts`, which is outside this change's ownership. It still
    derives from `breakpoints.xl`, so there is no second copy of the number —
    adding `atLeastXl` later is a one-line swap.
  */
  return useMediaQuery(`(min-width: ${breakpoints.xl}px)`);
}
